import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { timelineData } from '../data/portfolioData';
import { TimelineItem } from '../types';
import './WorkExperience.css';
import { FaBriefcase, FaGraduationCap, FaMapMarkerAlt, FaClock, FaTools, FaChevronDown } from 'react-icons/fa';

const ExpandedContent: React.FC<{
  item: TimelineItem;
  isVisible: boolean;
}> = ({ item, isVisible }) => {
  const variants = {
    open: { 
      opacity: 1, 
      height: 'auto',
      transition: { duration: 0.3 }
    },
    closed: { 
      opacity: 0, 
      height: 0,
      transition: { duration: 0.3 }
    }
  };

  return (
    <motion.div
      className="node-expanded"
      initial="closed"
      animate={isVisible ? "open" : "closed"}
      variants={variants}
    >
      <div className="technologies">
        <FaTools className="icon" />
        {item.technologies?.map((tech, i) => (
          <span key={i} className="tech-tag">
            {tech}
          </span>
        ))}
      </div>
      <ul className="achievements">
        {item.achievements.map((achievement, i) => (
          <motion.li
            key={i}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -20 }}
            transition={{ delay: i * 0.1 }}
          >
            {achievement}
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );
};

const TimelineNode: React.FC<{
  item: TimelineItem;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ item, index, isSelected, onSelect }) => {
  const isEven = index % 2 === 0;

  return (
    <motion.div
      className={`timeline-node ${isEven ? 'right' : 'left'} ${isSelected ? 'selected' : ''}`}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.2 }}
    >
      <motion.div
        className="node-content"
        whileHover={{ scale: 1.05 }}
        onClick={onSelect}
      >
        <div className="node-header">
          <img src={item.icon} alt={item.title} className="node-icon" />
          <h3>{item.title}</h3>
        </div>
        <div className="node-details">
          <p className="company">
            <FaBriefcase className="icon" /> {item.company}
          </p>
          <p className="location">
            <FaMapMarkerAlt className="icon" /> {item.location}
          </p>
          {item.period && (
            <p className="period">
              <FaClock className="icon" /> {item.period}
            </p>
          )}
        </div>
        <motion.div 
          className="expand-indicator"
          animate={{ rotate: isSelected ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <FaChevronDown />
        </motion.div>
      </motion.div>

      <ExpandedContent item={item} isVisible={isSelected} />
    </motion.div>
  );
};

const WorkExperience: React.FC = () => {
  const [selectedNode, setSelectedNode] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'work' | 'education'>('work');

  const handleNodeSelect = (index: number) => {
    setSelectedNode(selectedNode === index ? null : index);
  };

  return (
    <div className="experience-container">
      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="page-title"
      >
        Work Experience & Education
      </motion.h1>

      <div className="timeline-tabs">
        <motion.button
          className={`tab ${activeTab === 'work' ? 'active' : ''}`}
          onClick={() => setActiveTab('work')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaBriefcase /> Work Experience
        </motion.button>
        <motion.button
          className={`tab ${activeTab === 'education' ? 'active' : ''}`}
          onClick={() => setActiveTab('education')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaGraduationCap /> Education
        </motion.button>
      </div>

      <div className="timeline">
        <div className="timeline-line" />
        {timelineData[activeTab].map((item, index) => (
          <TimelineNode
            key={index}
            item={item as TimelineItem}
            index={index}
            isSelected={selectedNode === index}
            onSelect={() => handleNodeSelect(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default WorkExperience;
